import {
  type Texture,
  BackSide,
  BufferAttribute,
  BufferGeometry,
  Material,
  Matrix3,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
} from '@seddi/three';
import { create as createGeometryInternal } from '../core/bufferGeometry';
import { clone as cloneMaterial } from '../resources/material';
import { ResourcesContext } from '../resources/resourcesContext';
import { type GarmentGeometry, type GarmentGeometryPiece, SeamTapeGrain } from '@seddi/scene';
import { textureMapsKeys } from '../resources/textureTypes';

const MaterialDefault0 = new MeshStandardMaterial({ color: '#C4C4C4' });
const MaterialDefault1 = new MeshStandardMaterial({ color: '#C4C4C4' });
MaterialDefault1.side = BackSide;

export interface SeamTapeRenderable {
  readonly id: string;
  fabricRef?: string;
  garmentGeometry?: GarmentGeometry;
  readonly mesh: Mesh;
}

/**
 * Create seam tape.
 * @param id - ID.
 * @param parent - Parent.
 */
export const create = (id: string, parent: Object3D): SeamTapeRenderable => {
  const materials = [MaterialDefault1, MaterialDefault0]; // Back/Front
  const mesh = new Mesh(new BufferGeometry(), materials);
  mesh.name = `${id}-tape`;
  parent.add(mesh);
  return { id, mesh } as SeamTapeRenderable;
};

/**
 * Update geometry.
 * @param renderable - Renderable.
 * @param data - Geometry data.
 */
export const updateGeometry = (renderable: SeamTapeRenderable, data: GarmentGeometryPiece) => {
  renderable.mesh.geometry.dispose();
  // Create geometry.
  const geometry = createGeometryInternal({
    indices: data.indices,
    positions: data.position3D,
    uvs: data.position2D,
  });
  renderable.mesh.geometry = geometry;
  // Recreate sub-meshes.
  recreateGroups(renderable);
};

/**
 * Attach fabric.
 * @param renderable - Renderable.
 * @param id - Fabric ID.
 * @param resources - Resources context;
 */
export const attachFabric = (
  renderable: SeamTapeRenderable,
  id: string,
  grain: SeamTapeGrain,
  resources: ResourcesContext,
) => {
  if (renderable.fabricRef) {
    detachFabric(renderable, resources);
  }

  // Create material instances.
  const fabric = resources.getRefFabric(id);
  const material0 = cloneMaterial(fabric.back || fabric.front);
  const material1 = cloneMaterial(fabric.front);
  material0.side = BackSide;
  const materials = renderable.mesh.material as unknown as Material[];
  materials[0] = material0;
  materials[1] = material1;
  renderable.fabricRef = id;

  updateGrain(renderable, grain);
};

/**
 * Detach fabric.
 * @param renderable - Renderable.
 * @param resources - Resources context;
 */
export const detachFabric = (renderable: SeamTapeRenderable, resources: ResourcesContext) => {
  if (!renderable.fabricRef) {
    return;
  }
  const mesh = renderable.mesh;
  const materials = mesh.material as unknown as Material[];
  materials[0].dispose();
  materials[0] = MaterialDefault0;
  materials[1].dispose();
  materials[1] = MaterialDefault1;
  resources.retRefFabric(renderable.fabricRef);
  delete renderable.fabricRef;
};

/**
 * Update grain.
 * @param renderable - Renderable.
 * @param grain - Grain option.
 */
export const updateGrain = (renderable: SeamTapeRenderable, grain: SeamTapeGrain) => {
  let rotation = 0;
  switch (grain) {
    case SeamTapeGrain.BIAS:
      rotation = Math.PI / 4;
      break;
    case SeamTapeGrain.CROSS:
      rotation = Math.PI / 2;
      break;
  }

  const mesh = renderable.mesh;
  const materials = Array.isArray(mesh.material)
    ? (mesh.material as MeshPhysicalMaterial[])
    : [mesh.material as MeshPhysicalMaterial];

  // Reusable matrices
  const rotationMatrix = new Matrix3(),
    scaleMatrix = new Matrix3(),
    flipMatrix = new Matrix3(),
    tmpMatrix = new Matrix3();

  // Rotate by the negative grain orientation.
  rotationMatrix.makeRotation(-rotation);
  // For back-side textures, define a horizontal flip.
  flipMatrix.makeScale(-1.0, 1.0);

  // Update every texture key found on each material.
  for (const material of materials) {
    if (!material.userData?.fabric) {
      continue;
    }
    for (const key of textureMapsKeys) {
      const texture = material[key] as Texture;
      if (!texture) {
        continue;
      }
      // Create a scale matrix from the texture's repeat.
      scaleMatrix.makeScale(texture.repeat.x, texture.repeat.y);

      if (material.side === BackSide) {
        tmpMatrix.copy(flipMatrix);
        tmpMatrix.multiply(scaleMatrix);
      } else {
        tmpMatrix.copy(scaleMatrix);
      }

      tmpMatrix.multiply(rotationMatrix);

      texture.matrix.copy(tmpMatrix);
      texture.matrixAutoUpdate = false;
    }
  }
};

/**
 * Dispose seam tape.
 * @param renderable - Renderable.
 * @param resources - Resources manager.
 */

export const dispose = (renderable: SeamTapeRenderable, resources: ResourcesContext) => {
  detachFabric(renderable, resources);
  const mesh = renderable.mesh;
  mesh.parent.remove(mesh);
  mesh.geometry.dispose();
};

/**
 * Recreate geometry groups.
 * @param renderable - Renderable.
 */
const recreateGroups = (renderable: SeamTapeRenderable) => {
  const mesh = renderable.mesh;
  const geometry = mesh.geometry as BufferGeometry;
  // Choose the whole set of indices.
  const start = 0;
  const count = (geometry.index as BufferAttribute).count;
  geometry.clearGroups();
  // Render geometry several times with different materials.
  for (let i = 0; i < 2; ++i) {
    geometry.addGroup(start, count, i);
  }
};

const _matrix0 = new Matrix3();
const _matrix1 = new Matrix3();
const _matrix2 = new Matrix3();
const _matrix3 = new Matrix3();
